import { inflate, inflateRaw } from 'pako';

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function isPng(bytes: Uint8Array): boolean {
  return PNG_SIG.every((b, i) => bytes[i] === b);
}

function latin1(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]!);
  return s;
}

function asText(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  } catch {
    return latin1(bytes);
  }
}

function looksLikeDrawio(text: string): boolean {
  return text.includes('<mxfile') || text.includes('<mxGraphModel') || text.includes('mxGraphModel');
}

/** draw.io stores compressed page XML as URL-encoded deflate, then base64. */
export function decompressMxDiagram(payload: string): string | null {
  const trimmed = payload.trim().replace(/^<!\[CDATA\[/, '').replace(/\]\]>$/, '');
  if (!trimmed || trimmed.startsWith('<')) return trimmed || null;

  const tryDecode = (raw: string): string | null => {
    if (looksLikeDrawio(raw) && raw.includes('<')) return raw;
    try {
      const v = decodeURIComponent(raw);
      if (looksLikeDrawio(v) && v.includes('<')) return v;
    } catch {
      /* not URI-encoded */
    }
    return null;
  };

  const direct = tryDecode(trimmed);
  if (direct) return direct;

  let bin: Uint8Array;
  try {
    const b64 = trimmed.replace(/\s+/g, '');
    const ascii = atob(b64);
    bin = new Uint8Array(ascii.length);
    for (let i = 0; i < ascii.length; i += 1) bin[i] = ascii.charCodeAt(i);
  } catch {
    return null;
  }

  const inflateFns = [inflateRaw, inflate] as const;
  for (const fn of inflateFns) {
    try {
      const out = fn(bin);
      const text = asText(out);
      const decoded = tryDecode(text);
      if (decoded) return decoded;
      if (looksLikeDrawio(text)) return text;
    } catch {
      /* next algorithm */
    }
  }
  return null;
}

export function expandCompressedDrawioXml(xml: string): string {
  return xml.replace(/<diagram\b([^>]*)>([\s\S]*?)<\/diagram>/gi, (full, attrs: string, body: string) => {
    const trimmed = body.trim();
    if (trimmed.includes('<mxGraphModel') || trimmed.includes('&lt;mxGraphModel')) return full;
    const inner = decompressMxDiagram(trimmed);
    if (!inner) return full;
    return `<diagram${attrs}>${inner}</diagram>`;
  });
}

function parsePngTextChunk(type: string, data: Uint8Array): string | null {
  if (type === 'tEXt') {
    const z = data.indexOf(0);
    if (z < 0) return null;
    const keyword = latin1(data.subarray(0, z));
    if (keyword !== 'mxfile' && keyword !== 'mxGraphModel') return null;
    return asText(data.subarray(z + 1));
  }
  if (type === 'zTXt') {
    const z = data.indexOf(0);
    if (z < 0 || z + 2 > data.length) return null;
    const keyword = latin1(data.subarray(0, z));
    if (keyword !== 'mxfile' && keyword !== 'mxGraphModel') return null;
    const compressed = data.subarray(z + 2);
    try {
      return asText(inflate(compressed));
    } catch {
      try {
        return asText(inflateRaw(compressed));
      } catch {
        return null;
      }
    }
  }
  if (type === 'iTXt') {
    const z = data.indexOf(0);
    if (z < 0) return null;
    const keyword = latin1(data.subarray(0, z));
    if (keyword !== 'mxfile' && keyword !== 'mxGraphModel') return null;
    const rest = data.subarray(z + 1);
    const compressedFlag = rest[0] ?? 0;
    // skip flag + method + lang\0 + translated\0
    let i = 2;
    while (i < rest.length && rest[i] !== 0) i += 1;
    i += 1;
    while (i < rest.length && rest[i] !== 0) i += 1;
    i += 1;
    const textBytes = rest.subarray(i);
    if (compressedFlag === 1) {
      try {
        return asText(inflate(textBytes));
      } catch {
        return asText(inflateRaw(textBytes));
      }
    }
    return asText(textBytes);
  }
  return null;
}

export function extractDrawioXmlFromPng(bytes: Uint8Array): string | null {
  if (!isPng(bytes) || bytes.length < 24) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = latin1(bytes.subarray(offset + 4, offset + 8));
    const start = offset + 8;
    const end = start + length;
    if (end + 4 > bytes.length) break;
    const data = bytes.subarray(start, end);
    const text = parsePngTextChunk(type, data);
    if (text) {
      const decoded = decodeURIComponentSafe(text);
      if (looksLikeDrawio(decoded)) return decoded;
    }
    offset = end + 4;
  }
  const fallback = asText(bytes);
  const idx = fallback.indexOf('<mxfile');
  if (idx >= 0) return fallback.slice(idx);
  return null;
}

function decodeURIComponentSafe(text: string): string {
  try {
    if (text.includes('%3C') || text.includes('%3c')) return decodeURIComponent(text);
  } catch {
    /* keep */
  }
  return text;
}

export function extractDrawioXmlFromSvg(svg: string): string | null {
  const contentAttr = svg.match(/\bcontent=(["'])([\s\S]*?)\1/);
  if (contentAttr?.[2]) {
    const decoded = decodeURIComponentSafe(contentAttr[2].replace(/&amp;/g, '&').replace(/&quot;/g, '"'));
    if (looksLikeDrawio(decoded)) return decoded;
  }
  const mx = svg.match(/<mxfile[\s\S]*<\/mxfile>/i) ?? svg.match(/<mxGraphModel[\s\S]*<\/mxGraphModel>/i);
  if (mx?.[0]) return mx[0];
  return null;
}

/** Resolve draw.io XML from a file name + bytes or already-decoded text. */
export function extractDrawioXml(input: {
  bytes?: Uint8Array;
  text?: string;
  filename?: string;
}): string | null {
  const name = (input.filename ?? '').toLowerCase();
  const bytes = input.bytes;
  if (bytes && (name.endsWith('.png') || isPng(bytes))) {
    const fromPng = extractDrawioXmlFromPng(bytes);
    if (fromPng) return expandCompressedDrawioXml(fromPng);
  }
  const text = input.text ?? (bytes ? asText(bytes) : '');
  if (!text.trim()) return null;
  if (name.endsWith('.svg') || text.includes('<svg')) {
    const fromSvg = extractDrawioXmlFromSvg(text);
    if (fromSvg) return expandCompressedDrawioXml(fromSvg);
  }
  if (looksLikeDrawio(text)) return expandCompressedDrawioXml(text);
  return null;
}
